import {
  VOICE_AT_RISK_INACTIVITY_DAYS,
  type VoiceTenantOverviewSummary,
} from '@reach/shared-types/voice-tenant-overview';
import { AlertTriangle, Building2 } from 'lucide-react';
import React from 'react';
import { Card } from '../../../components/ui/card';
import { SummaryMetric } from '../SummaryMetric';
import { InfoTooltip } from '../../../components/ui/InfoTooltip';
import styles from './VoiceOverviewSummaryCards.module.css';

interface VoiceOverviewSummaryCardsProps {
  summary: VoiceTenantOverviewSummary | undefined;
  isLoading: boolean;
  isFailed: boolean;
  isFetching: boolean;
}

interface SummaryCardConfig {
  title: string;
  tooltip: string;
  iconClassName: string;
  icon: React.ReactNode;
  value: React.ReactNode;
}

export const VoiceOverviewSummaryCards: React.FC<VoiceOverviewSummaryCardsProps> = ({
  summary,
  isLoading,
  isFailed,
  isFetching,
}) => {
  const cards: SummaryCardConfig[] = [
    {
      title: 'Active Assist Tenants',
      tooltip:
        'Tenants with at least one active Assist agent. Cancelled and test tenants are not counted.',
      iconClassName: styles.summaryIconTenants,
      icon: <Building2 />,
      value: (summary?.voiceEnabledTenantCount ?? 0).toLocaleString(),
    },
    {
      title: 'At-Risk Tenants',
      tooltip: `Active tenants whose last call was more than ${VOICE_AT_RISK_INACTIVITY_DAYS} days ago, or whose first agent was created more than ${VOICE_AT_RISK_INACTIVITY_DAYS} days ago and have no recorded calls.`,
      iconClassName: styles.summaryIconAtRisk,
      icon: <AlertTriangle />,
      value: (summary?.atRiskTenantCount ?? 0).toLocaleString(),
    },
  ];

  return (
    <div
      className={styles.summaryGrid}
      data-fetching={isFetching ? 'true' : 'false'}
      aria-busy={isLoading}>
      {cards.map(card => (
        <Card
          key={card.title}
          className={styles.summaryCard}
          data-fetching={isFetching ? 'true' : 'false'}>
          <div className={styles.summaryCardHeader}>
            <div>
              <p className={styles.summaryTitle}>
                {card.title}
                <InfoTooltip label={card.tooltip}>{card.tooltip}</InfoTooltip>
              </p>
              <p className={styles.summaryValue}>
                <SummaryMetric
                  showDash={isFailed}
                  isLoading={isLoading}
                  skeletonClassName={styles.metricSkeleton}>
                  {card.value}
                </SummaryMetric>
              </p>
            </div>
            <div className={`${styles.summaryIcon} ${card.iconClassName}`}>{card.icon}</div>
          </div>
        </Card>
      ))}
    </div>
  );
};
